/**
 * The ONE SVG emitter for a PDApp icon — spec in, `<svg>` string out.
 *
 * Extracted from `shared/core-ui/ui/MiniappIcon.tsx` (#788) so the browser
 * renderer and the PDApp SDK draw from the same code instead of two copies
 * that agree only by review. (The server's PNG renderer,
 * `features/files/server/icon-render.ts`, is still its own copy — it composites
 * emoji artwork and rasterises through sharp, and rewiring the favicon path was
 * not part of #788. It remains the one place a drift can still happen, and its
 * comments say so.)
 *
 * DOM-free, React-free, zod-free, network-free. Everything the drawing needs
 * that would otherwise be fetched — a Lucide glyph's geometry, an uploaded
 * image's bytes — is PASSED IN. That is what lets the SDK render another app's
 * icon as a pure function: no permission check, no round trip, the same result
 * in Papan, in share view and for an anonymous visitor on pd.app.
 *
 * SECURITY: a spec's text and colours come from a manifest (semi-trusted) and
 * this markup is injected post-sanitise, so this module is a trust boundary.
 * Every text and attribute value is XML-escaped and every colour is checked
 * against a strict allowlist — a hostile `#000"/><script>…` cannot leave its
 * attribute.
 */
import {
  MINIAPP_ICON_DEFAULT_LUCIDE,
  PDAPP_GLYPH_NODE,
  autoTextColor,
  defaultIconSpecFor,
  glyphStrokeStyle,
  layoutIconText,
  stripPortableIconKeys,
  tileGeometry,
  tilePixelSize,
  type IconTextLayout,
  type MiniappIconSpec,
  type MiniappTileGeometry,
} from './miniapp-icon-core.js'
// From `tenant-path.js`, NOT `app-origin.js`: this module is compiled into the
// browser SDK, and that one reads `process.env`.
import { namedTenantPathPrefix } from './tenant-path.js'

/** A Lucide icon's geometry: `[tag, attributes]` pairs, as `lucide-react` exposes
 *  them — on `__iconNode` up to 1.46 and on `__iconData.node` from 1.47, a rename
 *  the two resolvers both read (`features/files/core/lucide-node.ts`,
 *  `shared/core-ui/ui/MiniappIcon.tsx`). It carries an unused third slot for
 *  children, which this accepts and ignores. Passed in, never fetched here. */
export type IconNodeEntry = readonly [tag: string, attrs: Record<string, string | number>, children?: unknown]
export type IconNode = readonly IconNodeEntry[]

/**
 * What a PURE renderer must be handed for a spec whose foreground is a NAMED
 * glyph (#788): the name AND its geometry, together.
 *
 * `node` is whatever the name resolved to, or `null` when it did not — an
 * unknown name, or the reserved PDApp mark, which is not a Lucide icon at all
 * and no resolver could ever supply. Both take the same answer, and it is the
 * one Papan itself gives: draw the DEFAULT MARK, under the DEFAULT NAME. The
 * name travels because the stroke style follows the geometry actually DRAWN
 * (`glyphStrokeStyle`) — keeping the asked-for name over the default mark paints
 * flat-capped corners with Lucide's round caps.
 *
 * The two shapes that look plausible and are not:
 *   • the name WITHOUT geometry — `renderIconSvgString` has nothing to draw, and
 *     its `defaultIconNode` is the CALLER's default, which a pure consumer of
 *     metadata does not have;
 *   • DROPPING both — the emitter only consults `defaultIconNode` on the
 *     named-glyph branch, so a spec with no foreground at all is drawn as a bare
 *     tile. (Found in a browser: a designed icon carrying the default mark came
 *     out as a plain coloured square.)
 *
 * Both producers of portable metadata — the server export and the browser's
 * produce mode — go through this, so they cannot answer it differently.
 */
export function portableGlyphFor(iconName: string, node: IconNode | null): { icon: string; glyph: IconNode } {
  return node ? { icon: iconName, glyph: node } : { icon: MINIAPP_ICON_DEFAULT_LUCIDE, glyph: PDAPP_GLYPH_NODE }
}

/** Uploaded icon art, already encoded as `data:` URIs by whoever had the right
 *  to read it. The files themselves are dot-prefixed and permission-gated, so
 *  an app can only ever draw what it was handed. */
export interface IconAssetImages {
  bg?: string
  fg?: string
}

export interface IconRenderModel {
  /** Tile silhouette + every box derived from it — the ONE source for viewBox,
   *  clip path and glyph/text/image placement. */
  geom: MiniappTileGeometry
  /** Spec resolved against the name-derived default — the RAW truth. */
  resolved: MiniappIconSpec
  /** The icon carries uploaded art (image background and/or foreground). */
  isAsset: boolean
  /** Background to DRAW. An image bg resolves to the name-derived gradient —
   *  the placeholder shown when no asset bytes were supplied. */
  bg: MiniappIconSpec['bg']
  emojiStyle: 'color' | 'mono'
  /** Foreground colour (Lucide stroke / text fill). RAW — validated on the way out. */
  fill: string
  /** Foreground text (empty when an icon or image is drawn, or when blank). */
  text: string
  /** Lucide icon name to draw, or null when text/an image is drawn, or blank. */
  iconName: string | null
  fontFamily: string
  /** Wrapped, auto-fitted text layout, or null when no text is drawn. */
  layout: IconTextLayout | null
}

/**
 * Resolve a spec into everything a renderer needs. Pure, and shared by the
 * React component and the string emitter so the two cannot draw differently.
 */
export function resolveIconRenderModel(
  spec: MiniappIconSpec | null | undefined,
  name: string,
): IconRenderModel {
  const resolved = spec ?? defaultIconSpecFor(name)
  const isAsset = resolved.bg.type === 'image' || resolved.image === true
  // An image bg has no drawable colour here — stand in the name-derived gradient.
  const bg = resolved.bg.type === 'image' ? defaultIconSpecFor(name).bg : resolved.bg
  const emojiStyle: 'color' | 'mono' = resolved.emojiStyle === 'mono' ? 'mono' : 'color'
  // Contrast is picked against the REAL bg (an image bg maps to white).
  const fill = resolved.textColor ?? autoTextColor(resolved.bg)
  // Foreground is AT MOST ONE of: image, Lucide icon, or text. Zero foreground
  // is BLANK — an intentional spec, not a hole to fill with the default glyph.
  const text = resolved.image ? '' : resolved.text ?? ''
  const iconName = resolved.image ? null : resolved.icon ?? null
  const fontFamily =
    emojiStyle === 'color'
      ? "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif, 'Noto Color Emoji'"
      : "'Noto Emoji', system-ui, sans-serif"
  const geom = tileGeometry(resolved.shape)
  const layout = iconName || !text ? null : layoutIconText(text, geom.text)
  return { geom, resolved, isAsset, bg, emojiStyle, fill, text, iconName, fontFamily, layout }
}

/** XML-escape for SVG text OR a double-quoted attribute value. */
export function xmlEscape(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

// Strict SVG-paint allowlist: hex (#rgb/#rgba/#rrggbb/#rrggbbaa) and rgb/rgba()
// with numeric or % components. Everything else — `url(…)`, a named colour
// carrying `;` or `"`, an injection payload — is rejected. A stored spec is
// always `#rrggbb`, so this is a no-op for real data and a gate for a hostile one.
const HEX_COLOR_RE = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/
const RGB_COLOR_RE =
  /^rgba?\(\s*[\d.]+%?\s*,\s*[\d.]+%?\s*,\s*[\d.]+%?\s*(?:,\s*[\d.]+%?\s*)?\)$/

/**
 * The ONE numeric value this emitter interpolates into markup.
 *
 * Every other value is guarded at draw time — colours through `safeColor`, text
 * and attributes through `xmlEscape`, images through `safeImageDataUri`, glyph
 * shapes through the tag/attribute allow-lists. `toStop` was going in raw on the
 * strength of "the schema validated it upstream", which is true of every
 * producer TODAY and is not something this module can check. A string here
 * closes the `<svg>` and emits live markup into an author's page,
 * post-sanitise — so it is clamped where it is used, like everything else.
 */
function safeStop(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 1 ? v : 1
}

/** `value` if it is a syntactically safe colour, else `fallback`. */
export function safeColor(value: string, fallback: string): string {
  const v = (value ?? '').trim()
  return HEX_COLOR_RE.test(v) || RGB_COLOR_RE.test(v) ? v : fallback
}

/** Only `data:image/png` and `data:image/jpeg` may be drawn — an SVG data URI
 *  would carry script into the picture, and a remote URL would make a "pure"
 *  render fetch something. */
const IMAGE_DATA_URI_RE = /^data:image\/(?:png|jpeg);base64,[A-Za-z0-9+/=]+$/

/** `uri` if it is an image data URI this renderer will draw, else null. */
export function safeImageDataUri(uri: string | undefined): string | null {
  if (typeof uri !== 'string') return null
  const v = uri.trim()
  return IMAGE_DATA_URI_RE.test(v) ? v : null
}

// Lucide art is on a 24-unit grid; the glyph box comes from `tileGeometry`
// (it differs per shape — the portrait tile sizes it off the NARROW axis).
export function glyphTransform(geom: MiniappTileGeometry): string {
  return `translate(${geom.glyph.x},${geom.glyph.y}) scale(${geom.glyph.dim / 24})`
}

/** Inline `<g>` markup for a Lucide node. */
/**
 * The shapes a glyph may be made of. Lucide geometry is only ever these, and an
 * allow-list is the difference between "draw a shape" and "emit an element".
 *
 * The emitter escapes a tag's CHARACTERS, not its meaning: `xmlEscape('script')`
 * is `script`, so without this an `iconNode` of `[['script', {…}]]` becomes a
 * live `<script>` in whatever document the drawing is pasted into. Since #788 a
 * drawing is written into an author's page as markup and rendered by the WORKER
 * from metadata that crossed a process, so the emitter is the last line rather
 * than a formality — and the callers that DO validate (the SDK) keep doing so.
 */
const GLYPH_SHAPE_TAGS: ReadonlySet<string> = new Set([
  'path', 'circle', 'ellipse', 'line', 'polyline', 'polygon', 'rect', 'g',
])

/**
 * Attributes a shape may carry: a plain name, no event handler, no reference
 * that could FETCH something.
 *
 * `on…` is the obvious one — an SVG graphics element fires `onload` in some
 * browsers, so `<path onload="…">` is executable in exactly the documents this
 * markup is pasted into. `href`/`xlink:href` are the quiet ones: they would let
 * a glyph pull in an external document, and the whole promise of an inserted
 * icon is that it needs no network. Lucide uses neither.
 */
function isDrawableAttr(name: string): boolean {
  if (!/^[a-zA-Z][a-zA-Z0-9-]*$/.test(name)) return false
  const k = name.toLowerCase()
  return !k.startsWith('on') && k !== 'href' && k !== 'xlink:href'
}

export function lucideGroupSvgString(
  node: IconNode,
  color: string,
  name: string,
  geom: MiniappTileGeometry,
): string {
  const els = node
    .filter(([tag]: IconNodeEntry) => GLYPH_SHAPE_TAGS.has(String(tag).toLowerCase()))
    .map(([tag, attrs]: IconNodeEntry) => {
      const a = Object.entries(attrs ?? {})
        .filter(([k]) => k !== 'key' && isDrawableAttr(k))
        .map(([k, v]) => `${xmlEscape(k)}="${xmlEscape(String(v))}"`)
        .join(' ')
      return `<${String(tag).toLowerCase()} ${a}/>`
    })
    .join('')
  const stroke = glyphStrokeStyle(name)
  return (
    `<g transform="${glyphTransform(geom)}" ` +
    `fill="none" stroke="${color}" stroke-width="2" ` +
    `stroke-linecap="${stroke.cap}" stroke-linejoin="${stroke.join}">` +
    els +
    `</g>`
  )
}

// Monotonic per-call id source: unique clip/gradient ids WITHOUT React's
// `useId`, so two icons in one document cannot collide on a `url(#id)`.
let svgIdCounter = 0

/** Names which slot a drawn `<image>` came from. Written so that reading art
 *  back OUT of a drawing is possible at all: the two `<image>` elements are
 *  otherwise told apart only by their geometry, which is not something to build
 *  on. See `extractInlinedIconAssets`. */
export const ASSET_ATTR = 'data-papan-asset'

export interface RenderIconSvgOptions {
  spec: MiniappIconSpec | null | undefined
  /** The app's name — the source of the default tile when `spec` is absent,
   *  and the picture's accessible label. */
  name: string
  /** Rendered size in CSS pixels (the tile's long axis). */
  size: number
  /** The Lucide glyph's geometry, when the spec names one. Absent ⇒ the
   *  reserved PDApp mark, the same fallback every other renderer takes. */
  iconNode?: IconNode | null
  /** The node for `MINIAPP_ICON_DEFAULT_LUCIDE`, used when `iconNode` is absent. */
  defaultIconNode?: IconNode | null
  /**
   * Token for this drawing's internal `url(#…)` ids (clip path, gradient).
   *
   * The default is a per-process counter, which is unique for everything drawn
   * by ONE page load. It is NOT unique for markup that is WRITTEN OUT and
   * accumulates: two icons inserted on different days both start at `0`, and in
   * one document the second `<clipPath id="miniapp-clip-0">` is ignored — so a
   * square tile inserted after a portrait one would be clipped to the portrait
   * squircle. Anything that emits markup for a file passes a random token.
   */
  uid?: string
  /** Uploaded art as data URIs. Absent ⇒ the spec's placeholder is drawn,
   *  which is what a caller without the bytes should show. */
  assets?: IconAssetImages
  /**
   * SERVER ONLY (#1015): the text foreground, already drawn — glyph outlines and
   * emoji art — in place of the `<text>` element. A browser draws `<text>` with
   * the reader's own fonts; a server has none it can rely on, so it draws the
   * letters itself from vendored fonts (`features/files/server/icon-text.ts`).
   * TRUSTED markup: it is produced from vendored fonts and art, never from a
   * spec's raw text, and it is never written into a page — only rasterised. Used
   * only on the text branch; an image or named glyph is drawn as usual.
   */
  textForeground?: string
  /**
   * Draw the background edge to edge with no squircle clip — a picture an OS or
   * a chat app rounds or crops itself (a home-screen icon, a preview card).
   */
  fullBleed?: boolean
}

/**
 * Render a spec to a self-contained SVG string: squircle clip, solid or
 * vertical-gradient background, and a foreground that is uploaded art, a
 * Lucide glyph, or centred auto-fitted text.
 */
export function renderIconSvgString(opts: RenderIconSvgOptions): string {
  const { spec, name } = opts
  const size = Number.isFinite(opts.size) && opts.size > 0 ? opts.size : 22
  const { geom, resolved, bg, fill, iconName, fontFamily, layout } = resolveIconRenderModel(spec, name)

  const uid = opts.uid ?? (svgIdCounter++).toString(36)
  const clipId = `miniapp-clip-${uid}`
  const gradId = `miniapp-grad-${uid}`

  const safeFill = safeColor(fill, '#ffffff')
  const bgImage = resolved.bg.type === 'image' ? safeImageDataUri(opts.assets?.bg) : null
  const fgImage = resolved.image === true ? safeImageDataUri(opts.assets?.fg) : null
  const bgFill = bg.type === 'solid' ? safeColor(bg.color, '#000000') : `url(#${gradId})`
  const grad =
    bg.type === 'gradient'
      ? `<linearGradient id="${gradId}" x1="0" y1="0" x2="0" y2="1">` +
        `<stop offset="0" stop-color="${safeColor(bg.from, '#000000')}"/>` +
        // Absent `toStop` ⇒ 1, the even ramp. Below 1, SVG extends the last
        // stop's colour to the bottom edge — that solid remainder is the point.
        `<stop offset="${safeStop(bg.toStop)}" stop-color="${safeColor(bg.to, '#000000')}"/>` +
        `</linearGradient>`
      : ''

  // Uploaded background covers the whole tile, under the foreground.
  const bgLayer = bgImage
    ? `<image ${ASSET_ATTR}="bg" href="${xmlEscape(bgImage)}" x="0" y="0" width="${geom.w}" height="${geom.h}" ` +
      `preserveAspectRatio="xMidYMid slice"/>`
    : ''

  let foreground: string
  if (fgImage) {
    foreground =
      `<image ${ASSET_ATTR}="fg" href="${xmlEscape(fgImage)}" x="${geom.image.x}" y="${geom.image.y}" ` +
      `width="${geom.image.dim}" height="${geom.image.dim}" preserveAspectRatio="xMidYMid meet"/>`
  } else if (iconName) {
    // An unknown name resolves to the default mark — and the stroke style must
    // follow the geometry actually DRAWN, or a typo'd name paints the flat-capped
    // PDApp mark with Lucide's round caps and quietly erases its corners.
    const node = opts.iconNode ?? opts.defaultIconNode ?? null
    const drawnName = opts.iconNode ? iconName : MINIAPP_ICON_DEFAULT_LUCIDE
    foreground = node ? lucideGroupSvgString(node, safeFill, drawnName, geom) : ''
  } else if (layout && opts.textForeground !== undefined) {
    foreground = opts.textForeground
  } else if (layout) {
    const n = layout.lines.length
    const firstY = geom.h / 2 - ((n - 1) / 2) * layout.lineHeight
    const tspans = layout.lines
      .map(
        (line, i) =>
          `<tspan x="${geom.w / 2}" y="${(firstY + i * layout.lineHeight).toFixed(2)}" ` +
          `dominant-baseline="central">${xmlEscape(line)}</tspan>`,
      )
      .join('')
    foreground =
      `<text text-anchor="middle" font-family="${xmlEscape(fontFamily)}" font-weight="600" ` +
      `font-size="${layout.fontSize}" fill="${safeFill}">${tspans}</text>`
  } else {
    foreground = '' // blank — background only
  }

  const px = tilePixelSize(geom.shape, size)
  return (
    // `xmlns` is NOT optional. Inside HTML the parser applies the SVG namespace
    // for us, so an inline injection works without it — but the moment the same
    // string is loaded as an IMAGE (a blob/data URL in `<img>`, which is how the
    // SDK's PNG path rasterises it, and how anyone would embed it in an email)
    // it is parsed as standalone XML and a missing namespace is a parse ERROR:
    // the image simply fails to load. Found by running the PNG path in a real
    // browser; every unit test stubbed `Image` and so never parsed it.
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${geom.w} ${geom.h}" width="${px.w}" height="${px.h}" role="img" aria-label="${xmlEscape(name)}">` +
    `<defs><clipPath id="${clipId}"><path d="${geom.clipPath}"/></clipPath>${grad}</defs>` +
    (opts.fullBleed ? `<g>` : `<g clip-path="url(#${clipId})">`) +
    `<rect x="0" y="0" width="${geom.w}" height="${geom.h}" fill="${bgFill}"/>` +
    bgLayer +
    foreground +
    `</g>` +
    `</svg>`
  )
}

// ───────────────────────────────────────────────────────────────────────────
// Portable metadata → markup (#788)
// ───────────────────────────────────────────────────────────────────────────

/** A portable icon spec: the manifest's own spec, plus the two things a pure
 *  renderer cannot fetch for itself (inlined art, and a named glyph's shape). */
export type PortableIconSpec = MiniappIconSpec & { assets?: IconAssetImages; glyph?: IconNode }

/** What an author embeds about a PDApp — the manifest subset #788 defines.
 *  `folderId`/`tenantId` are absent for an icon designed with no app behind it. */
export interface PortablePdappMetadata {
  name: string
  version?: string
  description?: string
  icon?: PortableIconSpec | null
  folderId?: string
  tenantId?: string
}

/**
 * Draw portable metadata — the ONE mapping from "what an author embedded" to
 * the emitter's options, so the SDK, the editor and the worker cannot disagree
 * about what a piece of metadata looks like.
 *
 * The reserved PDApp mark rides along as `defaultIconNode`: an app with no icon
 * in its metadata must draw the same tile Papan draws. It is suppressed when the
 * spec NAMES a glyph — a named glyph with no geometry draws its background
 * alone, because the wrong picture a substituted glyph would be is worse than a
 * bare tile. (Both producers of metadata avoid that shape entirely; see
 * `portableGlyphFor`.)
 */
export function renderPortableIconSvg(opts: {
  icon: PortableIconSpec | null | undefined
  name: string
  size: number
  /** See `RenderIconSvgOptions.uid` — required for markup that is written out. */
  uid?: string
}): string {
  const icon = opts.icon ?? null
  return renderIconSvgString({
    spec: icon,
    name: opts.name,
    size: opts.size,
    uid: opts.uid,
    iconNode: icon?.glyph ?? null,
    defaultIconNode: icon?.icon ? null : PDAPP_GLYPH_NODE,
    assets: icon?.assets,
  })
}

/** How tall an inserted icon is drawn, in CSS pixels, unless a caller says
 *  otherwise. Small enough to sit in a line of text or a button. */
export const PDAPP_ICON_DEFAULT_PX = 28

/** Marks the wrapper of an inserted icon. Design mode selects on it, and the
 *  metadata it carries is what makes "edit this icon" possible later. */
export const PDAPP_ICON_ATTR = 'data-papan-icon'

/** Marks a link the SDK turns into a LAUNCH. The href beside it is the
 *  no-SDK fallback, never the mechanism. */
export const PDAPP_OPEN_ATTR = 'data-papan-open-pdapp'

/** Escape for an HTML TEXT node: only what can start markup or an entity.
 *  `xmlEscape` is wrong here — it also escapes quotes, and `&apos;` renders as
 *  itself to a reader scanning the source. */
function htmlText(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}


/** Escape for a SINGLE-quoted HTML attribute.
 *
 *  Not `xmlEscape`: that also escapes `"`, and the values here are JSON, which
 *  is nothing but double quotes — `&quot;` on every one of them would triple the
 *  attribute and make the author's source unreadable for no gain. Single quotes
 *  and `&` are what can end the value or start an entity; `<`/`>` cannot break
 *  an attribute at all, and are escaped anyway so a naive sanitiser or a
 *  copy-paste into a different context has nothing to chew on. */
function attrEscape(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

/** The metadata an inserted icon carries, for editing it later.
 *
 *  `assets` and `glyph` are both dropped — everything that is already IN the
 *  drawing. A second base64 copy of the art would double the weight of exactly
 *  the icons that are heaviest, and a glyph's geometry is re-resolved from its
 *  NAME when someone edits the icon (`portableGlyphFor`), so carrying it is
 *  weight and a place for junk to sit rather than a fact anyone reads. The
 *  editor recovers the picture from the drawing itself. */
function editableMetadata(meta: PortablePdappMetadata, link: boolean): string {
  const icon = stripPortableIconKeys(meta.icon ?? {})
  return JSON.stringify({
    name: meta.name,
    ...(meta.version ? { version: meta.version } : {}),
    ...(meta.icon ? { icon } : {}),
    // Only when something USES them. An author who left the link off asked for a
    // picture; publishing the source app's ids to every visitor is not part of
    // that, and nothing in the markup reads them.
    ...(link && meta.folderId ? { folderId: meta.folderId } : {}),
    ...(link && meta.tenantId ? { tenantId: meta.tenantId } : {}),
  })
}

const SVG_OPEN = '<svg '

/** A token for one written-out drawing's internal ids. Random, not a counter:
 *  see `RenderIconSvgOptions.uid` — the document this lands in outlives the
 *  process that wrote it, and gains icons from other processes entirely. */
function randomUid(): string {
  return Math.random().toString(36).slice(2, 8)
}

export interface PdappIconMarkupOptions {
  /** Drawn height in CSS pixels. Default `PDAPP_ICON_DEFAULT_PX`. */
  size?: number
  /** Also OPEN the app it came from when clicked. Ignored without both ids —
   *  a designed icon has nowhere to go, so the option is unavailable rather
   *  than merely off, and no caller has to remember that. */
  link?: boolean
  /** The text beside a linked icon. Default `Open <name>`. */
  label?: string
}

/**
 * The block an author gets — markup, and nothing else (#788, Chu's review).
 *
 * It used to be a placeholder plus a `<script>` that called the SDK at runtime.
 * That cost a script parse, an SDK load and a DOM build per icon, re-ran on
 * every preview patch, and drew nothing at all where the SDK could not boot. The
 * drawing is a pure function of the metadata, so it belongs in the markup:
 *
 *   • **inline `<svg>`, not `<img>`** — inside an `<img>` an SVG is an isolated
 *     document that page CSS and web fonts do not reach, so a mono-emoji icon
 *     (whose face the PAGE loads) would render with the wrong glyph. Inline is
 *     also the smaller of the two, since base64 adds a third.
 *   • **an `<a href>` for the launch**, with the ids in an attribute the SDK
 *     delegates from. The href is what a plain browser does with it; inside
 *     Papan the SDK intercepts and the host resolves the target against the
 *     VIEWER's own access, unchanged.
 *   • **the metadata on the wrapper**, so the icon can be edited later.
 *
 * The result needs no SDK, no network and no permission question at runtime —
 * it draws the same in Papan, in a share view, for an anonymous pd.app visitor
 * and in a file opened from disk.
 */
export function buildPdappIconMarkup(
  meta: PortablePdappMetadata,
  opts: PdappIconMarkupOptions = {},
): string {
  const size = Number.isFinite(opts.size) && (opts.size as number) > 0 ? (opts.size as number) : PDAPP_ICON_DEFAULT_PX
  const link = !!opts.link && !!meta.folderId && !!meta.tenantId
  // The renderer writes width/height ATTRIBUTES, which a CSS box does not
  // override — so the drawing states its size in a STYLE too, and a later resize
  // (the design panel, or the author) wins over it. The emitter's output always
  // opens with exactly this prefix; if that ever stops being true the drawing is
  // still correct, just not restyleable, and a test pins the prefix.
  const drawn = renderPortableIconSvg({ icon: meta.icon, name: meta.name, size, uid: randomUid() })
  const svg = drawn.startsWith(SVG_OPEN)
    ? `${SVG_OPEN}style="height:${size}px;width:auto;display:block" ${drawn.slice(SVG_OPEN.length)}`
    : drawn
  const data = `${PDAPP_ICON_ATTR}='${attrEscape(editableMetadata(meta, link))}'`
  const box = `display:inline-flex;align-items:center;height:${size}px`
  if (!link) return `<span class="papan-pdapp-icon" ${data} style="${box}">${svg}</span>`
  // JSON.stringify, not a template: an id carrying a quote would otherwise make
  // the attribute unparseable and the launch silently dead.
  const target = JSON.stringify({ folderId: meta.folderId, tenantId: meta.tenantId })
  const label = htmlText(opts.label ?? `Open ${meta.name}`)
  // ROOT-RELATIVE, deliberately — and it only works because the serve origin
  // redirects it (`features/files/api/app-deep-link.ts`).
  //
  // An absolute href was the first fix for the 404 this used to give, and it
  // froze the INSERTING deployment's hostname into the author's page. Papan
  // ships one instance per client, so an app authored on one install and copied
  // to another then carried the first install's brand in its source and on
  // hover — a whitelabel leak that never breaks anything, because the SDK means
  // the href is rarely followed. Relative follows whatever host serves the page,
  // which is the only form that is correct on every install.
  //
  // ALWAYS the NAMED tenant (#853): a page has no reader of its own — anyone may
  // follow this link later — and `/~` is the READER's own drive, so a personal
  // tenant written as `/~` would send every other reader into THEIR drive with this
  // app's id. `/t/p_<owner>/…` opens it for the owner as their own drive and is
  // gated as whoever else follows it.
  //
  // The id is encoded BEFORE the call, not after: the helper interpolates it raw
  // (its other callers hold trusted ids), and here it arrives inside metadata
  // copied from another tenant's manifest.
  //
  // Under `/_to/`: a path Papan answers on an app's own host starts with `_`, so the
  // link never takes a path the app or its `serve.json` could use (owner, 2026-10-27).
  const href = `/_to${namedTenantPathPrefix(encodeURIComponent(meta.tenantId!))}/app/${encodeURIComponent(meta.folderId!)}`
  return [
    // `target="_blank"` is about the FALLBACK, not the normal path — with the SDK
    // present the click never navigates at all. Without it, a same-frame
    // navigation inside an app would replace the app with a page the frame's
    // sandbox cannot load (X-Frame-Options, no session): the author's app,
    // destroyed by their own icon. A new tab at least leaves it standing.
    `<a class="papan-pdapp-icon" target="_blank" rel="noopener"`,
    `   href="${xmlEscape(href)}"`,
    `   ${PDAPP_OPEN_ATTR}='${attrEscape(target)}' ${data}`,
    `   style="${box};gap:8px;text-decoration:none;color:inherit">${svg}<span>${label}</span></a>`,
  ].join('\n')
}

/**
 * Read uploaded art back OUT of a drawing — the inverse of what the emitter
 * above writes (#788, "edit this icon").
 *
 * An inserted icon carries its metadata WITHOUT `assets`: the art is in the
 * drawing, once. So editing one has to recover the picture from there, or the
 * author's own image disappears the moment they change a colour.
 *
 * Deliberately narrow: it reads only the shape this emitter produces, and only
 * `data:` URIs. Markup from anywhere else simply yields nothing, and the caller
 * then treats the picture as one the author must choose again.
 */
export function extractInlinedIconAssets(markup: string): { bg?: string; fg?: string } {
  const out: { bg?: string; fg?: string } = {}
  const re = new RegExp(`<image\\s+${ASSET_ATTR}="(bg|fg)"\\s+href="(data:[^"]+)"`, 'g')
  for (const m of markup.matchAll(re)) {
    const slot = m[1] as 'bg' | 'fg'
    // `xmlEscape` ran over the URI on the way out; `&amp;` is the only one of
    // its five that can appear in base64 or a mime type.
    if (!out[slot]) out[slot] = m[2].replace(/&amp;/g, '&')
  }
  return out
}

